import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Pencil } from "lucide-react";
import {
    adminListSubscriptionTiers,
    adminDeleteSubscriptionTier,
    adminGetAiCreditRates,
} from "../../lib/api";

// Sub-components (iter-105 refactor — was a 1,996-line monolith).
import { TierImage } from "./subscriptions/TierImage";
import { TierEditorDialog } from "./subscriptions/TierEditorDialog";
import { AiCreditRatesPanel } from "./subscriptions/AiCreditRatesPanel";
import { TierEconomicsHint } from "./subscriptions/TierEconomicsHint";
import { UserSubscriptionsPanel } from "./subscriptions/UserSubscriptionsPanel";
import { FreeTierCard } from "./subscriptions/FreeTierCard";
import { CreditPacksAdminPanel } from "./subscriptions/CreditPacksAdminPanel";
import { PromoCodesAdminPanel } from "./subscriptions/PromoCodesAdminPanel";

/**
 * Superadmin Subscriptions tab — composes the free-tier card, paid
 * subscription tiers list (with the editor dialog), AI credit-rate
 * editor, per-user subscriptions panel, credit packs admin, and
 * promo-code admin into a single screen. Each sub-panel manages its
 * own state + data fetching; this shell owns only the tiers list +
 * the editor / delete-confirm modals.
 */
export default function SubscriptionsTab() {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // tier obj or "new" or null
    const [pendingDelete, setPendingDelete] = useState(null);
    const [rates, setRates] = useState({});

    const refresh = async () => {
        setLoading(true);
        try {
            const [list, r] = await Promise.all([
                adminListSubscriptionTiers(),
                adminGetAiCreditRates(),
            ]);
            setTiers(list);
            setRates(r);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load tiers.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refresh();
    }, []);

    const handleDelete = async () => {
        if (!pendingDelete) return;
        try {
            await adminDeleteSubscriptionTier(pendingDelete.id);
            toast.success("Tier deleted");
            setPendingDelete(null);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete.");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-subscriptions">
            {/* iter-96: free-tier card */}
            <FreeTierCard />
            {/* Tiers panel */}
            <section
                className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden"
                data-testid="subscription-tiers-panel"
            >
                <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#1C253C]">
                    <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#00A3FF]" />
                        Subscription tiers
                    </h2>
                    <button
                        type="button"
                        onClick={() => setEditing("new")}
                        data-testid="btn-new-tier"
                        className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white"
                    >
                        <Plus className="w-3 h-3 inline mr-1" />
                        New tier
                    </button>
                </header>
                <div className="p-5">
                    {loading ? (
                        <div className="text-sm text-slate-500">Loading…</div>
                    ) : tiers.length === 0 ? (
                        <div
                            className="text-sm text-slate-400 px-3 py-4 border border-dashed border-[#2A3655] rounded-sm"
                            data-testid="tiers-empty"
                        >
                            No tiers yet — click "New tier" to create your
                            first subscription plan.
                        </div>
                    ) : (
                        <ul className="space-y-3" data-testid="tiers-list">
                            {tiers.map((t) => (
                                <li
                                    key={t.id}
                                    className={`p-3 bg-[#0F1B3A]/60 border rounded-sm ${
                                        t.active
                                            ? "border-[#2A3655]"
                                            : "border-[#2A3655] opacity-60"
                                    }`}
                                    data-testid={`tier-row-${t.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <TierImage
                                            storagePath={t.image_storage_path}
                                            alt={t.title}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-white truncate">
                                                    {t.title}
                                                </h3>
                                                {!t.active && (
                                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-400">
                                                        Inactive
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-slate-500 font-mono">
                                                    #{t.sort_order}
                                                </span>
                                            </div>
                                            {t.description && (
                                                <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                                                    {t.description}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1.5 text-xs">
                                                <span className="text-emerald-400 font-bold">
                                                    ${Number(t.price_usd).toFixed(2)}
                                                </span>
                                                <span className="text-slate-500">
                                                    every {t.duration_days}d
                                                </span>
                                                <span className="text-[#00A3FF] font-semibold">
                                                    {Number(t.credit_amount).toLocaleString()}{" "}
                                                    credits
                                                </span>
                                                {t.revenuecat_product_id && (
                                                    <span className="text-slate-500 font-mono text-[10px]">
                                                        rc:{t.revenuecat_product_id}
                                                    </span>
                                                )}
                                            </div>
                                            <TierEconomicsHint
                                                tier={t}
                                                rates={rates}
                                            />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setEditing(t)}
                                                data-testid={`tier-edit-${t.id}`}
                                                aria-label="Edit tier"
                                                className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#007AFF]/10"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPendingDelete(t)}
                                                data-testid={`tier-delete-${t.id}`}
                                                aria-label="Delete tier"
                                                className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* AI rates panel */}
            <AiCreditRatesPanel />

            {/* User subscriptions management */}
            <UserSubscriptionsPanel tiers={tiers} />

            {/* iter-95: credit packs + promo codes */}
            <CreditPacksAdminPanel />
            <PromoCodesAdminPanel tiers={tiers} />

            {/* Editor dialog */}
            <TierEditorDialog
                open={editing !== null}
                onClose={() => setEditing(null)}
                tier={editing && editing !== "new" ? editing : null}
                onSaved={() => refresh()}
            />

            {/* Delete confirm */}
            {pendingDelete && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
                    onClick={() => setPendingDelete(null)}
                    data-testid="tier-delete-overlay"
                >
                    <div
                        className="w-full max-w-md bg-[#0F1B3A] border border-[#2A3655] rounded-md shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-3 border-b border-[#2A3655] bg-[#0A1128] text-sm font-bold uppercase tracking-wider text-white">
                            Delete tier
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-slate-300">
                                Delete{" "}
                                <span className="text-white font-semibold">
                                    {pendingDelete.title}
                                </span>
                                ? Users already subscribed will keep access
                                until their period ends; new sign-ups won't
                                see it.
                            </p>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#2A3655] bg-[#0A1128]">
                            <button
                                type="button"
                                onClick={() => setPendingDelete(null)}
                                data-testid="tier-delete-cancel"
                                className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                data-testid="tier-delete-confirm"
                                className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-red-600 hover:bg-red-500 text-white"
                            >
                                <Trash2 className="w-3 h-3 inline mr-1" />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
